import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PenSquare, Eye, Search, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatWIT } from "@/lib/appApi";
import { StatusBadge, STATUS_LABELS } from "@/lib/adminConst";

export default function ContentList() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const status = params.get("status") || "";

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (status) p.status = status;
    if (q) p.q = q;
    try { const r = await api.get("/articles", { params: p }); setItems(r.data); } finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (s) => { const p = new URLSearchParams(params); if (s) p.set("status", s); else p.delete("status"); setParams(p); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-3xl font-extrabold text-dlh-green">Daftar Berita</h1>
        <Link to="/admin/konten/baru" className="inline-flex items-center gap-2 bg-dlh-green text-white px-4 py-2 rounded-lg font-semibold hover:bg-dlh-greenDark"><PenSquare className="h-4 w-4" /> Tulis Berita</Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip active={!status} onClick={() => setStatus("")}>Semua</FilterChip>
        {["draft", "needs_revision", "published", "archived"].map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => setStatus(s)}>{STATUS_LABELS[s]}</FilterChip>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari judul…" data-testid="content-search"
          className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dlh-orange" />
      </div>

      {loading ? <p className="text-gray-500">Memuat…</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500">Belum ada artikel.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="content-table">
              <thead className="bg-slate-50 text-left text-gray-500">
                <tr><th className="p-3 font-semibold">Judul</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold hidden md:table-cell">Diperbarui</th><th className="p-3 font-semibold text-right">Aksi</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50" data-testid={`content-row-${a.id}`}>
                    <td className="p-3">
                      <Link to={`/admin/konten/${a.id}`} className="font-semibold text-dlh-green hover:text-dlh-orange leading-snug">{a.title || "(Tanpa judul)"}</Link>
                      {a.is_demo && <span className="ml-2 text-[10px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">DEMO</span>}
                    </td>
                    <td className="p-3"><StatusBadge status={a.status} /></td>
                    <td className="p-3 text-gray-500 hidden md:table-cell">{formatWIT(a.updated_at)}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {a.status === "published" && (() => {
                          const url = `${window.location.origin}/berita/${a.slug}`;
                          const ogUrl = `${window.location.origin}/api/og/${a.slug}?u=${encodeURIComponent(window.location.origin)}`;
                          return (
                            <>
                              <a href={`https://wa.me/?text=${encodeURIComponent(a.title + " " + ogUrl)}`} target="_blank" rel="noreferrer"
                                onClick={(e) => e.stopPropagation()} data-testid={`share-wa-${a.id}`}
                                className="p-2 rounded hover:bg-green-50 text-[#25D366]" title="Bagikan ke WhatsApp"><MessageCircle className="h-4 w-4" /></a>
                              <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Tautan berita disalin"); }}
                                data-testid={`share-copy-${a.id}`} className="p-2 rounded hover:bg-dlh-greenPale text-gray-600" title="Salin tautan"><Link2 className="h-4 w-4" /></button>
                            </>
                          );
                        })()}
                        <Link to={`/admin/konten/${a.id}/pratinjau`} className="p-2 rounded hover:bg-dlh-greenPale text-gray-600" title="Pratinjau"><Eye className="h-4 w-4" /></Link>
                        <Link to={`/admin/konten/${a.id}`} className="p-2 rounded hover:bg-dlh-greenPale text-gray-600" title="Edit" data-testid={`edit-${a.id}`}><PenSquare className="h-4 w-4" /></Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-dlh-green text-white border-dlh-green" : "bg-white text-gray-600 border-gray-300 hover:border-dlh-green"}`}>{children}</button>
  );
}
